'use client';
import React, { useState } from 'react';
import WalletBalanceCards from './WalletBalanceCards';
import WalletBalanceChart from './WalletBalanceChart';
import TransactionTable from './TransactionTable';
import DepositModal from './DepositModal';
import WithdrawModal from './WithdrawModal';
import { Plus, ArrowUpRight, RefreshCw, Globe } from 'lucide-react';

export default function WalletContent() {
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [currency, setCurrency] = useState<'USD' | 'EUR' | 'INR'>('USD');

  const currencySymbols: Record<string, string> = { USD: '$', EUR: '€', INR: '₹' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Wallet</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            Manage your funds, deposits, withdrawals, and transaction history
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Currency selector */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
            <Globe size={14} style={{ color: 'var(--muted-foreground)' }} />
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'USD' | 'EUR' | 'INR')}
              className="text-sm bg-transparent outline-none font-semibold"
              style={{ color: 'var(--foreground)' }}
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="INR">INR (₹)</option>
            </select>
          </div>

          <button
            className="flex items-center gap-2 p-2 rounded-lg transition-colors hover:bg-muted"
            aria-label="Refresh wallet"
          >
            <RefreshCw size={16} style={{ color: 'var(--muted-foreground)' }} />
          </button>

          <button onClick={() => setWithdrawOpen(true)} className="btn-ghost py-2 px-4">
            <ArrowUpRight size={16} />
            Withdraw
          </button>
          <button onClick={() => setDepositOpen(true)} className="btn-primary py-2 px-4">
            <Plus size={16} />
            Add Funds
          </button>
        </div>
      </div>

      {/* Balance cards */}
      <WalletBalanceCards currency={currency} symbol={currencySymbols[currency]} />

      {/* Chart + quick stats */}
      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <WalletBalanceChart currency={currency} symbol={currencySymbols[currency]} />
        </div>
        <div className="space-y-4">
          <WalletQuickStats currency={currency} symbol={currencySymbols[currency]} />
        </div>
      </div>

      {/* Transactions */}
      <TransactionTable currency={currency} symbol={currencySymbols[currency]} />

      {/* Modals */}
      {depositOpen && <DepositModal onClose={() => setDepositOpen(false)} currency={currency} symbol={currencySymbols[currency]} />}
      {withdrawOpen && <WithdrawModal onClose={() => setWithdrawOpen(false)} currency={currency} symbol={currencySymbols[currency]} />}
    </div>
  );
}

function WalletQuickStats({ currency, symbol }: { currency: string; symbol: string }) {
  const stats = [
    { id: 'wqs-1', label: 'Lifetime Winnings', value: `${symbol}1,840.00`, color: '#10B981' },
    { id: 'wqs-2', label: 'Lifetime Deposits', value: `${symbol}980.00`, color: '#7C3AED' },
    { id: 'wqs-3', label: 'Total Withdrawn', value: `${symbol}695.50`, color: '#06B6D4' },
    { id: 'wqs-4', label: 'Competitions Entered', value: '48', color: '#F59E0B' },
    { id: 'wqs-5', label: 'Entry Fees Paid', value: `${symbol}240.00`, color: '#F43F5E' },
    { id: 'wqs-6', label: 'Referral Earnings', value: `${symbol}85.00`, color: '#A78BFA' },
  ];

  return (
    <div className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <h3 className="text-sm font-bold text-foreground mb-4">Lifetime Summary</h3>
      <div className="space-y-3">
        {stats.map((s) => (
          <div key={s.id} className="flex items-center justify-between">
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{s.label}</span>
            <span className="text-sm font-bold" style={{ color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}